import { Capacitor } from "@capacitor/core";
import { ENV } from "../environments/environment";

/**
 *  /!\ the url scheme must be all lowercase, without spaces or special characters
 */
const DEFAULT_SLUG = "mentalplus";
const DEFAULT_URL_SCHEME = "mentalplus";

/**
 * First path segments that are route keywords, NOT study slugs. Used when parsing
 * the slug out of the URL in multi-study mode so a slug-less path (e.g. "/welcome")
 * doesn't get mistaken for a study slug.
 */
const RESERVED_SEGMENTS = new Set(["welcome", "tabs"]);

/** True when this deployment serves exactly one study. Native is always single-study. */
export function isSingleStudy(): boolean {
  if (Capacitor.isNativePlatform()) return true;
  return ENV.STUDY.mode === "single";
}

/**
 * The pinned numeric study id in single-study mode (so we skip the slug -> id lookup);
 * null in multi-study mode, where the id is resolved at runtime via the welcome call.
 */
export function getConfiguredStudyId(): number | null {
  if (!isSingleStudy()) return null;
  if (typeof ENV.STUDY.id !== "number") {
    throw new Error(
      "Single-study deployment is misconfigured: ENV.STUDY.id must be a number"
    );
  }
  return ENV.STUDY.id;
}

/**
 * The pinned is_gamified flag in single-study mode (so we skip the is_gamified call);
 * null in multi-study mode, where it is fetched per study via the API.
 */
export function getConfiguredIsGamified(): boolean | null {
  if (!isSingleStudy()) return null;
  return ENV.STUDY.is_gamified;
}

/** Read the leading path segment as a slug, ignoring reserved route keywords. SSR-safe. */
function parseSlugFromPath(): string | null {
  if (typeof window === "undefined") return null;
  const [, first] = window.location.pathname.split("/");
  if (!first || RESERVED_SEGMENTS.has(first)) return null;
  return first;
}

export function getStudySlug(): string {
  // Single-study (and native): always the configured slug — the URL needs no slug.
  if (isSingleStudy()) {
    return ENV.STUDY.slug ?? DEFAULT_SLUG;
  }
  // Multi-study (web): the slug comes from the URL; fall back so first load still resolves.
  return parseSlugFromPath() ?? ENV.STUDY.slug ?? DEFAULT_SLUG;
}

export function getUrlScheme() {
  return DEFAULT_URL_SCHEME.toLowerCase();
}
